import { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { DataGrid } from "@mui/x-data-grid";
import { Button, Chip } from "@mui/material";
import Loader from "../../../components/Loader/Loader";
import PageShell from "../../../components/layout/PageShell";
import PageHead from "../../../components/layout/PageHead";
import "../../../components/common/css/datatable.scss";

/**
 * Wire reconciliation — non-Canadian investors, and everyone paying a USD
 * campaign, pay by international wire quoting their per-investment payment
 * reference (RFM-…). When the credit shows up at the bank, match it here and
 * mark the order PAID with the bank's reference. Investors can pre-declare
 * "wire sent" (with an amount, a currency and an optional receipt) from the
 * payment step; declared rows float to the top.
 *
 * ── CURRENCY (2026-08-02) ──────────────────────────────────────────────────
 * This screen is the one moment a human matches a bank credit to an order, and
 * it used to show neither the order's currency nor the campaign's — just a bare
 * `amount` with no formatter. Under the no-conversion policy a campaign is
 * listed in CAD or USD and wire carries all USD volume plus every
 * non-Canadian's CAD payment, so a reconciler was looking at a number with no
 * reliable unit and settling on it.
 *
 * Three separate figures are now shown, because the whole point of holding them
 * is that they can disagree:
 *   ORDERED   what the order is denominated in (derived server-side from the
 *             campaign since the order-currency change).
 *   DECLARED  what the INVESTOR says they sent.
 *   RECEIVED  what the BANK actually credited, keyed here.
 *
 * markWirePaid REQUIRES the received amount and currency and refuses a currency
 * that disagrees with the campaign unless an explicit reason is recorded, so
 * this form cannot be simplified back into a single bank-reference prompt
 * without breaking settlement outright.
 */
const API = process.env.REACT_APP_API;

function authHeaders() {
  return { "x-access-token": localStorage.getItem("accessToken") };
}

/**
 * "4,000.00 USD" — always an explicit ISO code, never a symbol.
 *
 * Intl's currency style under en-CA renders CAD as a bare "$" and USD as "US$".
 * On a screen whose entire job is telling those two apart, a bare "$" is worse
 * than no formatting at all. DECIMAL columns arrive from the API as strings, so
 * the Number() is load-bearing.
 */
function money(amount, currency) {
  if (amount === null || amount === undefined || amount === "") return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return String(amount);
  const figure = n.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency ? `${figure} ${currency}` : `${figure} (no currency)`;
}

function escapeHtml(value) {
  return String(value === null || value === undefined ? "" : value).replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );
}

const WireReconciliation = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("UNPAID");

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}admin/wire/list`,
        { paymentStatus: statusFilter || undefined },
        { headers: authHeaders() }
      );
      setRows(res?.data?.investments || []);
    } catch (error) {
      Swal.fire({
        title: error?.response?.data?.message || "Failed to load wire queue",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  /**
   * Post one attempt. A mismatched currency comes back 409
   * RECEIVED_CURRENCY_MISMATCH; the caller re-posts with a reason.
   */
  const postMarkPaid = (payload) =>
    axios.post(`${API}admin/wire/mark-paid`, payload, { headers: authHeaders() });

  const markPaid = async (row) => {
    // The currency options offered are NOT restricted to the campaign's. The
    // reconciler is recording what the bank did, and the case worth catching is
    // exactly the one where that is not what the campaign expects.
    const options = Array.from(
      new Set(
        ["CAD", "USD", row.payableCurrency, row.campaignCurrency, row.currency].filter(
          Boolean
        )
      )
    );
    const { value: form } = await Swal.fire({
      title: `Mark #${row.investmentId} as PAID`,
      width: 620,
      html: `
        <div style="text-align:left;font-size:14px">
          <p style="margin:0 0 10px">
            Order: <b>${escapeHtml(money(row.amount, row.currency))}</b><br/>
            This campaign is funded in <b>${escapeHtml(
              row.payableCurrency || row.campaignCurrency || "an undeclared currency"
            )}</b>.<br/>
            ${
              row.declaredAmount != null && row.declaredCurrency
                ? `The investor stated they sent <b>${escapeHtml(
                    money(row.declaredAmount, row.declaredCurrency)
                  )}</b>.`
                : "The investor did not state an amount."
            }
          </p>
          <label for="wire-bank-ref" style="display:block;margin-top:8px">
            Bank reference of the incoming wire
            (investor quoted ${escapeHtml(row.referenceCode || "no code")})
          </label>
          <input id="wire-bank-ref" class="swal2-input" style="width:100%;margin:4px 0"
                 placeholder="e.g. FT26080200123" />
          <label for="wire-received-amount" style="display:block;margin-top:8px">
            Amount the bank actually credited
          </label>
          <input id="wire-received-amount" class="swal2-input" type="number" step="0.01" min="0"
                 style="width:100%;margin:4px 0" placeholder="Plain number, no separators" />
          <label for="wire-received-currency" style="display:block;margin-top:8px">
            Currency the bank actually credited
          </label>
          <select id="wire-received-currency" class="swal2-input" style="width:100%;margin:4px 0">
            <option value="">Select a currency</option>
            ${options
              .map((c) => `<option value="${escapeHtml(c)}">${escapeHtml(c)}</option>`)
              .join("")}
          </select>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Mark paid",
      preConfirm: () => {
        const bankReference = document.getElementById("wire-bank-ref").value.trim();
        const receivedAmount = document
          .getElementById("wire-received-amount")
          .value.trim();
        const receivedCurrency = document.getElementById("wire-received-currency").value;
        if (!bankReference) {
          Swal.showValidationMessage("The bank reference is required.");
          return false;
        }
        if (!receivedAmount || !Number.isFinite(Number(receivedAmount))) {
          Swal.showValidationMessage(
            "Enter the credited amount as a plain number, without separators."
          );
          return false;
        }
        if (!receivedCurrency) {
          Swal.showValidationMessage(
            "Select the currency the bank credited — record what arrived, not what was ordered."
          );
          return false;
        }
        return { bankReference, receivedAmount, receivedCurrency };
      },
    });
    if (!form) return;

    // A short credit is normal (a correspondent bank took its fee) and the API
    // records it rather than refusing, so the confirmation is here — the
    // reconciler should see the discrepancy before it becomes a settled order.
    const ordered = Number(row.amount);
    const credited = Number(form.receivedAmount);
    if (Number.isFinite(ordered) && Number.isFinite(credited) && ordered !== credited) {
      const delta = (credited - ordered).toFixed(2);
      const ok = await Swal.fire({
        icon: "warning",
        title: "The credit does not match the order",
        html: `Ordered <b>${escapeHtml(money(row.amount, row.currency))}</b>, credited
               <b>${escapeHtml(money(form.receivedAmount, form.receivedCurrency))}</b>
               (${escapeHtml(delta)}).<br/>Settling will mark the order PAID in full.`,
        showCancelButton: true,
        confirmButtonText: "Settle anyway",
      });
      if (!ok.isConfirmed) return;
    }

    setLoading(true);
    try {
      await postMarkPaid({ investmentId: row.investmentId, ...form });
      await fetchRows();
    } catch (error) {
      const data = error?.response?.data;
      if (data?.code === "RECEIVED_CURRENCY_MISMATCH") {
        // Never a silent pass. The reason is written to the audit log with the
        // discrepancy, so the decision can be reconstructed later.
        setLoading(false);
        const { value: reason } = await Swal.fire({
          icon: "warning",
          title: "Wrong currency credited",
          html: `The bank credited <b>${escapeHtml(data.receivedCurrency)}</b> but this
                 campaign is funded in <b>${escapeHtml(data.expectedCurrency)}</b>.<br/>
                 Settling anyway is recorded as an override. Say why.`,
          input: "textarea",
          inputPlaceholder: "Why is this credit correct despite the currency?",
          showCancelButton: true,
          confirmButtonText: "Override and settle",
          inputValidator: (v) =>
            !v || !v.trim() ? "A reason is required to override." : undefined,
        });
        if (!reason) return;
        setLoading(true);
        try {
          await postMarkPaid({
            investmentId: row.investmentId,
            ...form,
            currencyOverrideReason: reason.trim(),
          });
          await fetchRows();
        } catch (overrideError) {
          Swal.fire({
            title:
              overrideError?.response?.data?.message || "Failed to mark as paid",
            icon: "error",
            confirmButtonText: "OK",
          });
        } finally {
          setLoading(false);
        }
        return;
      }
      Swal.fire({
        title: data?.message || "Failed to mark as paid",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadProof = async (row) => {
    try {
      const res = await axios.get(`${API}admin/wire/proof/${row.investmentId}`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wire-proof-${row.investmentId}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      Swal.fire({ title: "No proof available", icon: "info", confirmButtonText: "OK" });
    }
  };

  const columns = [
    { field: "investmentId", headerName: "ID", width: 80 },
    { field: "investor", headerName: "Investor", flex: 1, minWidth: 150 },
    { field: "campaign", headerName: "Campaign", flex: 1, minWidth: 160 },
    {
      field: "amount",
      headerName: "Ordered",
      width: 150,
      // The unit travels with the figure. Sorting still works on the raw value
      // because renderCell only changes what is drawn.
      renderCell: (params) => money(params.value, params.row.currency),
    },
    {
      field: "currency",
      headerName: "Order ccy",
      width: 105,
      renderCell: (params) =>
        params.row.currencyMismatch ? (
          <Chip label={params.value || "none"} color="error" size="small" />
        ) : (
          params.value || "—"
        ),
    },
    {
      field: "campaignCurrency",
      headerName: "Campaign ccy",
      width: 130,
      // A grandfathered EUR/GBP/HKD listing is payable in USD, so both are
      // shown when they differ rather than picking one and misleading on the
      // other.
      renderCell: (params) => {
        const listed = params.value || "—";
        const payable = params.row.payableCurrency;
        return payable && payable !== params.value ? `${listed} → ${payable}` : listed;
      },
    },
    { field: "referenceCode", headerName: "Reference", width: 140 },
    {
      field: "declaredSentAt",
      headerName: "Declared sent",
      width: 140,
      renderCell: (params) =>
        params.value ? (
          <Chip label={new Date(params.value).toLocaleDateString()} color="info" size="small" />
        ) : (
          "—"
        ),
    },
    {
      field: "declaredAmount",
      headerName: "Investor says",
      width: 150,
      renderCell: (params) => money(params.value, params.row.declaredCurrency),
    },
    {
      field: "receivedAmount",
      headerName: "Bank credited",
      width: 150,
      renderCell: (params) => money(params.value, params.row.receivedCurrency),
    },
    { field: "paymentStatus", headerName: "Status", width: 100 },
    { field: "bankReference", headerName: "Bank ref", width: 140 },
    {
      field: "actions",
      headerName: "Actions",
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {params.row.hasProof && (
            <Button size="small" variant="outlined" onClick={() => downloadProof(params.row)}>
              Proof
            </Button>
          )}
          {params.row.paymentStatus !== "PAID" && (
            <Button size="small" variant="contained" color="success" onClick={() => markPaid(params.row)}>
              Mark paid
            </Button>
          )}
        </div>
      ),
    },
  ];

  const mismatched = rows.filter((r) => r.currencyMismatch).length;

  return (
    <PageShell>
      <PageHead title="Wire reconciliation" />
      <div className="datatable">
        <Loader isLoading={loading} />
        <div className="datatableTitle">
          <h5>Wire transfers</h5>
          <div>
            <label htmlFor="wire-status" style={{ fontSize: 14, marginRight: 8 }}>
              Status
            </label>
            <select
              id="wire-status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{ padding: "6px 12px", borderRadius: 4, border: "1px solid #ccc" }}
            >
              <option value="UNPAID">UNPAID</option>
              <option value="PAID">PAID</option>
              <option value="">All</option>
            </select>
          </div>
        </div>
        {mismatched > 0 && (
          <p role="alert" style={{ color: "#b71c1c", fontWeight: 600, margin: "0 0 8px" }}>
            {mismatched} order{mismatched === 1 ? " is" : "s are"} denominated in a
            currency this campaign is not funded in. These predate the denomination
            gate and cannot be settled — escalate rather than overriding.
          </p>
        )}
        <DataGrid
          className="datagrid"
          autoHeight
          rows={rows}
          getRowId={(r) => r.investmentId}
          getRowClassName={(params) =>
            params.row.currencyMismatch ? "wire-currency-mismatch" : ""
          }
          sx={{
            "& .wire-currency-mismatch": {
              backgroundColor: "rgba(211,47,47,0.08)",
            },
          }}
          columns={columns}
          hideFooterPagination
        />
      </div>
    </PageShell>
  );
};

export default WireReconciliation;
